import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  FileText, 
  Sparkles, 
  LogOut,
  Menu,
  X,
  Users,
  Music,
  ListMusic,
  Zap,
  Send,
  Disc,
  Video,
  ClipboardCheck,
  Film,
  BarChart3,
  Shield,
  Youtube as YoutubeIcon
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { path: "/admin", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/admin/automation", icon: Zap, label: "Automação" },
  { path: "/admin/aprovacoes", icon: ClipboardCheck, label: "Aprovações", showBadge: true },
  { path: "/admin/articles", icon: FileText, label: "Matérias" },
  { path: "/admin/submissions", icon: Send, label: "Divulgações" },
  { path: "/admin/musics", icon: Music, label: "Músicas / CDs" },
  { path: "/admin/dvds", icon: Video, label: "DVDs" },
  { path: "/admin/cd-submissions", icon: Disc, label: "Vitrine CDs" },
  { path: "/admin/video-submissions", icon: Film, label: "Vitrine Vídeos" },
  { path: "/admin/youtube", icon: YoutubeIcon, label: "YouTube Sync" },
  { path: "/admin/stats", icon: BarChart3, label: "Estatísticas" },
  { path: "/admin/artists", icon: Users, label: "Artistas" },
  { path: "/admin/ai-monitor", icon: Sparkles, label: "Sites" },
  { path: "/admin/backup", icon: Shield, label: "Backup" },
];

export default function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPending = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        // Lightweight count endpoint - returns only number, not full articles list
        const res = await fetch(`${API}/api/articles/pending-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPendingCount(typeof data?.count === "number" ? data.count : 0);
        }
      } catch (err) {
        console.warn("Pending count fetch failed", err);
      }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000); // refresh every minute
    return () => clearInterval(interval);
    // Don't refetch on every navigation - cached interval handles it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-[#0A0A0A] border-r border-white/10">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-white/10">
          <Link to="/" className="flex items-center">
            <img
              src="/arrochaplay-logo.png?v=11"
              alt="ArrochaPlay"
              className="h-8 w-auto"
              draggable="false"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`admin-sidebar-item ${isActive ? "active" : "text-zinc-400 hover:text-white"}`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon size={20} />
                <span className="flex-1">{item.label}</span>
                {item.showBadge && pendingCount > 0 && (
                  <span className="ml-auto bg-[#FF007F] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center" data-testid="pending-badge">
                    {pendingCount > 99 ? "99+" : pendingCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="admin-sidebar-item text-zinc-400 hover:text-red-500 w-full"
            data-testid="logout-btn"
          >
            <LogOut size={20} />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-[#0A0A0A] border-r border-white/10 animate-slide-in-right">
            <div className="h-16 flex items-center justify-between px-6 border-b border-white/10">
              <Link to="/" className="flex items-center">
                <img
                  src="/arrochaplay-logo.png?v=11"
                  alt="ArrochaPlay"
                  className="h-8 w-auto"
                  draggable="false"
                />
              </Link>
              <button onClick={() => setSidebarOpen(false)} className="text-white">
                <X size={24} />
              </button>
            </div>
            <nav className="p-4 space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`admin-sidebar-item ${isActive ? "active" : "text-zinc-400"}`}
                  >
                    <item.icon size={20} />
                    <span className="flex-1">{item.label}</span>
                    {item.showBadge && pendingCount > 0 && (
                      <span className="ml-auto bg-[#FF007F] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                        {pendingCount > 99 ? "99+" : pendingCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
            <div className="p-4 border-t border-white/10">
              <button
                onClick={handleLogout}
                className="admin-sidebar-item text-zinc-400 hover:text-red-500 w-full"
              >
                <LogOut size={20} />
                <span>Sair</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar - Mobile */}
        <header className="lg:hidden h-16 bg-[#0A0A0A] border-b border-white/10 flex items-center justify-between px-4">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="text-white p-2"
          >
            <Menu size={24} />
          </button>
          <Link to="/" className="flex items-center">
            <img
              src="/arrochaplay-logo.png?v=11"
              alt="ArrochaPlay"
              className="h-7 w-auto"
              draggable="false"
            />
          </Link>
          <div className="w-10" />
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
